import { LoaderFunctionArgs } from 'react-router-dom';
import {
  AccountFieldsFragment,
  AgencyUserFieldsFragment,
  BusinessPolicyFieldsFragment,
  CallFieldsFragment,
  CallTranscriptFieldsFragment,
  CaraWorkspaceFieldsFragment,
  CaraWorkspaceOverviewFieldsFragment,
  CertificateOfLiabilityInsuranceFieldsFragment,
  CustomPresetFieldsFragment,
  EmailThreadFieldsFragment,
  GetAccountDocument,
  GetAccountQuery,
  GetAccountQueryVariables,
  GetAccountsDocument,
  GetAccountsQuery,
  GetAccountsQueryVariables,
  GetAgencyUserDocument,
  GetAgencyUserQuery,
  GetAgencyUserQueryVariables,
  GetAgencyUsersDocument,
  GetAgencyUsersQuery,
  GetAgencyUsersQueryVariables,
  GetBusinessPoliciesDocument,
  GetBusinessPoliciesQuery,
  GetBusinessPoliciesQueryVariables,
  GetBusinessPolicyDocument,
  GetBusinessPolicyQuery,
  GetBusinessPolicyQueryVariables,
  GetCallDocument,
  GetCallQuery,
  GetCallQueryVariables,
  GetCallsDocument,
  GetCallsQuery,
  GetCallsQueryVariables,
  GetCaraWorkspaceDocument,
  GetCaraWorkspaceQuery,
  GetCaraWorkspaceQueryVariables,
  GetCaraWorkspacesDocument,
  GetCaraWorkspacesQuery,
  GetCaraWorkspacesQueryVariables,
  GetCertificateDocument,
  GetCertificateQuery,
  GetCertificateQueryVariables,
  GetCustomPresetDocument,
  GetCustomPresetQuery,
  GetCustomPresetQueryVariables,
  GetCustomPresetsDocument,
  GetCustomPresetsQuery,
  GetCustomPresetsQueryVariables,
  GetEmailThreadDocument,
  GetEmailThreadQuery,
  GetEmailThreadQueryVariables,
  GetEmailThreadsDocument,
  GetEmailThreadsQuery,
  GetEmailThreadsQueryVariables,
  GetHtmlDocumentTemplateDocument,
  GetHtmlDocumentTemplateQuery,
  GetHtmlDocumentTemplateQueryVariables,
  GetHtmlDocumentTemplatesDocument,
  GetHtmlDocumentTemplatesQuery,
  GetHtmlDocumentTemplatesQueryVariables,
  GetInsuranceCarriersDocument,
  GetInsuranceCarriersQuery,
  GetInsuranceCarriersQueryVariables,
  GetIntegrationDocument,
  GetIntegrationQuery,
  GetIntegrationQueryVariables,
  GetIntegrationsDocument,
  GetIntegrationsQuery,
  GetIntegrationsQueryVariables,
  GetKnowledgeBaseDocument,
  GetKnowledgeBaseDocumentDocument,
  GetKnowledgeBaseDocumentQuery,
  GetKnowledgeBaseDocumentQueryVariables,
  GetKnowledgeBaseQuery,
  GetKnowledgeBaseQueryVariables,
  GetKnowledgeBasesDocument,
  GetKnowledgeBasesQuery,
  GetKnowledgeBasesQueryVariables,
  GetPolicyExplorerConversationDocument,
  GetPolicyExplorerConversationQuery,
  GetPolicyExplorerConversationQueryVariables,
  GetServiceRequestDocument,
  GetServiceRequestQuery,
  GetServiceRequestQueryVariables,
  GetServiceRequestsDocument,
  GetServiceRequestsQuery,
  GetServiceRequestsQueryVariables,
  HtmlDocumentTemplateFieldsFragment,
  InsuranceCarrierFieldsFragment,
  IntegrationFieldsFragment,
  KnowledgeBaseDocumentFieldsFragment,
  KnowledgeBaseFieldsFragment,
  LoggedInAgencyUserFieldsFragment,
  PolicyExplorerConversationMessageFieldsFragment,
  ServiceRequestFieldsFragment
} from './graphql';
import { client } from './client';
import { query } from '@oysterjs/corev2/graphql/loaders';
import * as apm from '@oysterjs/corev2/apm';

export const getAgencyUser = async () =>
  query<GetAgencyUserQuery, GetAgencyUserQueryVariables, LoggedInAgencyUserFieldsFragment>(
    client,
    { query: GetAgencyUserDocument },
    async (data) => {
      if (data.agencyUser) {
        await apm.setUser({
          id: data.agencyUser.id,
          email: data.agencyUser.email,
          name: data.agencyUser.name,
          agencyId: data.agencyUser.agency.id,
          agencyName: data.agencyUser.agency.name
        });
      }

      return data.agencyUser;
    },
    '/app/signin'
  );

export const getAgencyUsers = async () =>
  query<GetAgencyUsersQuery, GetAgencyUsersQueryVariables, AgencyUserFieldsFragment[]>(
    client,
    { query: GetAgencyUsersDocument },
    (data) => data.agencyUsers,
    '/app/signin'
  );

export const getAccounts = async () =>
  query<GetAccountsQuery, GetAccountsQueryVariables, AccountFieldsFragment[]>(
    client,
    { query: GetAccountsDocument },
    (data) => data.accounts,
    '/app/accounts'
  );

export const getAccount = async ({ params }: LoaderFunctionArgs) =>
  query<GetAccountQuery, GetAccountQueryVariables, AccountFieldsFragment>(
    client,
    { query: GetAccountDocument, variables: { id: params.id || '' } },
    (data) => data.account,
    '/app/accounts'
  );

export const getInsuranceCarriers = async () =>
  query<
    GetInsuranceCarriersQuery,
    GetInsuranceCarriersQueryVariables,
    InsuranceCarrierFieldsFragment[]
  >(
    client,
    { query: GetInsuranceCarriersDocument, fetchPolicy: 'cache-first' },
    (data) => data.carriers,
    '/app/signin'
  );

export const getBusinessPolicies = async () =>
  query<
    GetBusinessPoliciesQuery,
    GetBusinessPoliciesQueryVariables,
    BusinessPolicyFieldsFragment[]
  >(client, { query: GetBusinessPoliciesDocument }, (data) => data.businessPolicies, '/policies');

export const getBusinessPolicy = async ({ params }: LoaderFunctionArgs) =>
  query<GetBusinessPolicyQuery, GetBusinessPolicyQueryVariables, BusinessPolicyFieldsFragment>(
    client,
    { query: GetBusinessPolicyDocument, variables: { id: params.id || '' } },
    (data) => data.businessPolicy,
    '/app/policies'
  );

export const getPolicyExplorerConversation = async ({ params }: LoaderFunctionArgs) =>
  query<
    GetPolicyExplorerConversationQuery,
    GetPolicyExplorerConversationQueryVariables,
    PolicyExplorerConversationMessageFieldsFragment[]
  >(
    client,
    {
      query: GetPolicyExplorerConversationDocument,
      variables: { policyId: params.id || '' }
    },
    (data) => data.policyExplorerConversation
  );

export const getCertificate = async ({ params }: LoaderFunctionArgs) =>
  query<
    GetCertificateQuery,
    GetCertificateQueryVariables,
    CertificateOfLiabilityInsuranceFieldsFragment
  >(
    client,
    {
      query: GetCertificateDocument,
      variables: { id: params.certificateId || '' }
    },
    (data) => data.certificate
  );

export const getServiceRequests = async () =>
  query<GetServiceRequestsQuery, GetServiceRequestsQueryVariables, ServiceRequestFieldsFragment[]>(
    client,
    { query: GetServiceRequestsDocument },
    (data) => data?.serviceRequests || []
  );

export const getServiceRequest = async ({ params }: LoaderFunctionArgs) =>
  query<GetServiceRequestQuery, GetServiceRequestQueryVariables, ServiceRequestFieldsFragment>(
    client,
    { query: GetServiceRequestDocument, variables: { id: params.id || '' } },
    (data) => data.serviceRequest,
    '/app/servicing'
  );

export const getCalls = async () =>
  query<GetCallsQuery, GetCallsQueryVariables, CallFieldsFragment[]>(
    client,
    { query: GetCallsDocument },
    (data) => data.calls
  );

export const getCall = async ({ params }: LoaderFunctionArgs) =>
  query<GetCallQuery, GetCallQueryVariables, CallFieldsFragment & CallTranscriptFieldsFragment>(
    client,
    { query: GetCallDocument, variables: { id: params.id || '' } },
    (data) => data.call
  );

export const getEmailThreads = async () =>
  query<GetEmailThreadsQuery, GetEmailThreadsQueryVariables, EmailThreadFieldsFragment[]>(
    client,
    { query: GetEmailThreadsDocument },
    (data) => data.emailThreads
  );

export const getEmailThread = async ({ params }: LoaderFunctionArgs) =>
  query<GetEmailThreadQuery, GetEmailThreadQueryVariables, EmailThreadFieldsFragment>(
    client,
    { query: GetEmailThreadDocument, variables: { id: params.id || '' } },
    (data) => data.emailThread
  );

export const getCaraWorkspaces = async () =>
  query<
    GetCaraWorkspacesQuery,
    GetCaraWorkspacesQueryVariables,
    CaraWorkspaceOverviewFieldsFragment[]
  >(client, { query: GetCaraWorkspacesDocument }, (data) => data?.caraWorkspaces || []);

export const getCaraWorkspace = async ({ params }: LoaderFunctionArgs) =>
  query<GetCaraWorkspaceQuery, GetCaraWorkspaceQueryVariables, CaraWorkspaceFieldsFragment>(
    client,
    { query: GetCaraWorkspaceDocument, variables: { id: params.id || '' } },
    (data) => data.caraWorkspace
  );

export const getCustomPresets = async () =>
  query<GetCustomPresetsQuery, GetCustomPresetsQueryVariables, CustomPresetFieldsFragment[]>(
    client,
    { query: GetCustomPresetsDocument },
    (data) => data.customPresets
  );

export const getCustomPreset = async ({ params }: LoaderFunctionArgs) =>
  query<GetCustomPresetQuery, GetCustomPresetQueryVariables, CustomPresetFieldsFragment>(
    client,
    { query: GetCustomPresetDocument, variables: { id: params.id || '' } },
    (data) => data.customPreset
  );

export const getHtmlDocumentTemplates = async () =>
  query<
    GetHtmlDocumentTemplatesQuery,
    GetHtmlDocumentTemplatesQueryVariables,
    HtmlDocumentTemplateFieldsFragment[]
  >(client, { query: GetHtmlDocumentTemplatesDocument }, (data) => data.htmlDocumentTemplates);

export const getHtmlDocumentTemplate = async ({ params }: LoaderFunctionArgs) =>
  query<
    GetHtmlDocumentTemplateQuery,
    GetHtmlDocumentTemplateQueryVariables,
    HtmlDocumentTemplateFieldsFragment
  >(
    client,
    { query: GetHtmlDocumentTemplateDocument, variables: { id: params.id || '' } },
    (data) => data.htmlDocumentTemplate
  );

export const getKnowledgeBases = async () =>
  query<GetKnowledgeBasesQuery, GetKnowledgeBasesQueryVariables, KnowledgeBaseFieldsFragment[]>(
    client,
    { query: GetKnowledgeBasesDocument },
    (data) => data.knowledgeBases
  );

export const getKnowledgeBase = async ({ params }: LoaderFunctionArgs) =>
  query<GetKnowledgeBaseQuery, GetKnowledgeBaseQueryVariables, KnowledgeBaseFieldsFragment>(
    client,
    { query: GetKnowledgeBaseDocument, variables: { id: params.id || '' } },
    (data) => data.knowledgeBase
  );

export const getDefaultKnowledgeBase = async () => {
  const knowledgeBases = await getKnowledgeBases();
  return query<GetKnowledgeBaseQuery, GetKnowledgeBaseQueryVariables, KnowledgeBaseFieldsFragment>(
    client,
    { query: GetKnowledgeBaseDocument, variables: { id: knowledgeBases?.[0]?.id || '' } },
    (data) => data.knowledgeBase
  );
};

export const getKnowledgeBaseDocument = async ({ params }: LoaderFunctionArgs) =>
  query<
    GetKnowledgeBaseDocumentQuery,
    GetKnowledgeBaseDocumentQueryVariables,
    KnowledgeBaseDocumentFieldsFragment
  >(
    client,
    { query: GetKnowledgeBaseDocumentDocument, variables: { id: params.id || '' } },
    (data) => data.knowledgeBaseDocument
  );

export const getIntegrations = async () =>
  query<GetIntegrationsQuery, GetIntegrationsQueryVariables, IntegrationFieldsFragment[]>(
    client,
    { query: GetIntegrationsDocument },
    (data) => data.integrations
  );

export const getIntegration = async ({ params }: LoaderFunctionArgs) =>
  query<GetIntegrationQuery, GetIntegrationQueryVariables, IntegrationFieldsFragment>(
    client,
    { query: GetIntegrationDocument, variables: { id: params.id || '' } },
    (data) => data.integration
  );
